import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, MailX, Loader2 } from 'lucide-react';
import { API_BASE } from '../utils/apiBase';

const API = API_BASE;

/**
 * Manual unsubscribe form. Users typically land here via the footer link
 * inside emails (which pre-fills the email). This page also handles the
 * "success" state after a one-click unsub via the token link.
 */
const UnsubscribePage = () => {
  const [searchParams] = useSearchParams();
  const prefilledEmail = (searchParams.get('e') || '').trim().toLowerCase();
  const tokenClickSuccess = searchParams.get('done') === '1';

  const [email, setEmail] = useState(prefilledEmail);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(tokenClickSuccess);

  useEffect(() => {
    document.title = 'Unsubscribe · Karma Kundali';
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/api/unsubscribe/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase(), reason: reason.trim() || null }),
      });
      if (!r.ok) throw new Error('Could not unsubscribe. Please email care@karmakundali.org');
      setDone(true);
    } catch (e2) {
      alert(e2.message || 'Failed to unsubscribe');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="unsubscribe-success-page" className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
        <div className="max-w-lg w-full bg-white rounded-3xl border-2 border-brand-gold/40 shadow-premium p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-700 mx-auto mb-3" />
          <h1 className="font-playfair font-bold text-2xl text-brand-maroon mb-2">Aap unsubscribe ho gaye</h1>
          <p className="text-brand-text-soft text-sm">
            Aage se aapko koi bhi reminder ya promotional email nahi jayega.
          </p>
          <p className="text-brand-text-mute text-xs mt-4">
            Sirf aapke <strong>order-related emails</strong> (Kundali PDF delivery, payment confirmation) jayenge kyunki wo aapki service ka hissa hain.
          </p>
          <a href="/" className="inline-block mt-6 text-brand-maroon underline text-sm">
            Website pe wapas jayein
          </a>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="unsubscribe-page" className="min-h-screen bg-brand-cream flex items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="max-w-lg w-full bg-white rounded-3xl border-2 border-brand-gold/40 shadow-premium p-8 space-y-4">
        <div className="text-center">
          <MailX className="w-12 h-12 text-brand-maroon mx-auto mb-2" />
          <h1 className="font-playfair font-bold text-2xl text-brand-maroon mb-2">Unsubscribe</h1>
          <p className="text-brand-text-soft text-sm">
            Reminder aur promotional emails bandh karne ke liye apna email confirm kijiye.
          </p>
        </div>

        <label className="block">
          <span className="block text-xs font-semibold text-brand-text-dark mb-1.5 tracking-wider uppercase">Aapka email</span>
          <input
            data-testid="unsubscribe-input-email"
            type="email" required value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="aapka@example.com"
            className="w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm text-brand-text-dark"
          />
        </label>

        <label className="block">
          <span className="block text-xs font-semibold text-brand-text-dark mb-1.5 tracking-wider uppercase">Reason (optional)</span>
          <textarea
            data-testid="unsubscribe-input-reason"
            rows={3} maxLength={200} value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Aap kyun unsubscribe kar rahe hain? (help karega hamen improve karne mein)"
            className="w-full px-4 py-3 rounded-xl bg-white border-2 border-brand-gold/30 focus:border-brand-gold focus:outline-none text-sm text-brand-text-dark"
          />
        </label>

        <button
          type="submit"
          disabled={submitting}
          data-testid="unsubscribe-submit-btn"
          className="w-full inline-flex items-center justify-center gap-2 bg-brand-maroon hover:bg-brand-maroon-dark disabled:opacity-60 text-brand-gold-light font-bold px-6 py-3.5 rounded-full transition-all"
        >
          {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Processing…</> : 'Unsubscribe kar dein'}
        </button>

        <p className="text-center text-xs text-brand-text-mute">
          Order-related emails (Kundali delivery, payment confirmation) fir bhi jayenge.
        </p>
      </form>
    </div>
  );
};

export default UnsubscribePage;
